import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  LockOpen,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCcw,
  TriangleAlert,
  Trash2,
} from "lucide-react";

import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { CheckboxField, Field, NumberField, SelectField, TextField } from "@/components/admin/fields";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/components/feedback/ToastHost";
import { DataTable } from "@/components/data/DataTable";
import { EmptyState } from "@/components/feedback/EmptyState";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { listAudit } from "@/services/api/audit";
import { useTransitionLoad } from "@/hooks/useLoads";
import { listCustomers } from "@/services/api/customers";
import { listHauliers } from "@/services/api/hauliers";
import { listCse } from "@/services/api/cse";
import { listSites } from "@/services/api/sites";
import { listTrailers } from "@/services/api/trailers";
import { statusLabel } from "@/lib/statusBadge";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";
import { useAuth } from "@/stores/authStore";
import {
  createLoad,
  deleteLoad,
  getLoad,
  listLoads,
  updateLoad,
  type LoadCollectionInput,
  type LoadCreate,
} from "@/services/api/loads";
import type { AuditLog, Load } from "@/services/api/types";

const NEXT_BY_STATUS: Record<string, string[]> = {
  draft: ["locked"],
  locked: ["booked", "dispatched"],
  booked: ["dispatched"],
  dispatched: ["completed"],
  completed: [],
};

/** Replay audit changes to reconstruct the load fields at a point in time. */
function snapshotAt(entries: AuditLog[], at: string): Record<string, string> {
  const snapshot: Record<string, string> = {};
  const target = new Date(at).getTime();
  for (const entry of entries) {
    if (new Date(entry.occurred_at).getTime() > target) break;
    if (entry.action === "insert" || entry.action === "update") {
      for (const change of entry.changes) {
        if (change.new_value !== null && change.new_value !== undefined) {
          snapshot[change.field_name] = change.new_value;
        } else {
          delete snapshot[change.field_name];
        }
      }
    }
  }
  return snapshot;
}

function tomorrowIso(): string {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function todayIso(): string {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/** Shift an ISO date (YYYY-MM-DD) by a signed number of days. */
function addDays(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function nowTime(): string {
  const date = new Date();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function emptyCollection(): LoadCollectionInput {
  return {
    site_id: null,
    trailer_id: null,
    pallet_count: null,
    cartons: null,
    collection_reference: "",
    collection_date: tomorrowIso(),
    collection_time: nowTime(),
    wip_time: "",
    wip_ready: false,
    preloads: "",
    comments: "",
    weight_tonnes: null,
    non_standard_pallets: false,
    tallest_pallet_height: null,
    shipping_request_numbers: "",
    customer_reference_numbers: "",
  };
}

const emptyForm: LoadCreate = {
  load_ref: "",
  customer_id: 0,
  direction: "delivery",
  delivery_date: tomorrowIso(),
  arrival_time: nowTime(),
  collections: [emptyCollection()],
};

export interface LoadsPageProps {
  /** "delivery" (default, /admin/loads) or "collection" (/admin/collections). */
  direction?: "delivery" | "collection";
}

export function LoadsPage({ direction = "delivery" }: LoadsPageProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperadmin = user?.is_superadmin === true;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [day, setDay] = useState(todayIso);
  const limit = 25;
  const isCollection = direction === "collection";
  const [selected, setSelected] = useState<Load | null>(null);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [deleting, setDeleting] = useState<Load | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<LoadCreate>(emptyForm);
  const [timeline, setTimeline] = useState(100);
  // Audit deep-link context (?r=<id>&a=<action>&f=<fields>&ar=<auditId>).
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);
  const [highlight, setHighlight] = useState<string | null>(searchParams.get("r"));
  const limitEx = 500;

  const query = useQuery({
    queryKey: ["loads", "admin", offset, day, direction],
    queryFn: () => listLoads({ limit, offset, day: day || undefined, direction }),
  });
  const transition = useTransitionLoad();
  const customers = useQuery({ queryKey: ["customers", "all"], queryFn: () => listCustomers({ limit: limitEx }) });
  const hauliers = useQuery({ queryKey: ["hauliers", "all"], queryFn: () => listHauliers({ limit: limitEx }) });
  const cses = useQuery({ queryKey: ["cse", "all"], queryFn: () => listCse({ limit: limitEx }) });
  const sites = useQuery({ queryKey: ["sites", "all"], queryFn: () => listSites({ limit: limitEx }) });
  const trailers = useQuery({ queryKey: ["trailers", "all"], queryFn: () => listTrailers({ limit: limitEx }) });
  const selectedDetail = useQuery({
    queryKey: ["loads", selected?.id],
    queryFn: () => (selected ? getLoad(selected.id) : Promise.resolve(null)),
    enabled: selected !== null,
  });
  const history = useQuery({
    queryKey: ["load-history", selected?.id],
    queryFn: () =>
      selected
        ? listAudit({ entity_type: "load", entity_id: String(selected.id), limit: 500 })
        : Promise.resolve({ items: [], total: 0, limit: 500, offset: 0 }),
    enabled: selected !== null,
  });

  const entries = useMemo(() => {
    const items = [...(history.data?.items ?? [])];
    items.sort((a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime());
    return items;
  }, [history.data]);

  const timelineStart = entries.length ? new Date(entries[0].occurred_at).getTime() : 0;
  const timelineEnd = entries.length
    ? Math.max(timelineStart, new Date(entries[entries.length - 1].occurred_at).getTime())
    : 0;
  const timelineValue = timelineStart + ((timelineEnd - timelineStart) * timeline) / 100;
  const snapshot = entries.length
    ? snapshotAt(entries, new Date(timelineValue).toISOString())
    : {};
  const hasHistory = entries.length > 1;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["loads"] });
    void queryClient.invalidateQueries({ queryKey: ["load-counters"] });
  };

  // Step the day filter by a signed number of days (starts from today when no
  // day filter is active, mirroring the load scheduler's navigation).
  const shiftDay = (delta: number) => {
    setDay(addDays(day || todayIso(), delta));
    setOffset(0);
  };

  const toCreatePayload = (f: LoadCreate): LoadCreate => ({
    ...f,
    arrival_time: f.arrival_time ? f.arrival_time : null,
    delivery_date: f.delivery_date ? f.delivery_date : null,
    collections: (f.collections ?? []).map((c) => ({
      ...c,
      collection_date: c.collection_date ? c.collection_date : null,
      collection_time: c.collection_time ? c.collection_time : null,
      wip_time: c.wip_ready ? null : c.wip_time ? c.wip_time : null,
    })),
  });

  const saveCreate = useMutation({
    mutationFn: () => createLoad(toCreatePayload(form)),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      setCreating(false);
      setForm({ ...emptyForm, direction });
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const saveUpdate = useMutation({
    mutationFn: (load: Load) =>
      updateLoad(load.id, toCreatePayload(form), load.row_version),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      setSelected(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteLoad(id),
    onSuccess: () => {
      toast.success(t("admin.deleted"));
      setDeleting(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const openLoad = (load: Load, openMode: "view" | "edit") => {
    setForm({
      load_ref: load.load_ref,
      customer_id: load.customer_id,
      direction: (load.direction as "delivery" | "collection") ?? direction,
      haulier_id: load.haulier_id,
      cse_id: load.cse_id,
      destination_address: load.destination_address ?? "",
      postcode: load.postcode ?? "",
      delivery_date: load.delivery_date ?? "",
      arrival_time: load.arrival_time ?? "",
      depot_reference_number: load.depot_reference_number ?? "",
      invoice_reference: load.invoice_reference ?? "",
      collections: (load.collections ?? []).length
        ? load.collections.map((c) => ({
            site_id: c.site_id,
            trailer_id: c.trailer_id,
            pallet_count: c.pallet_count,
            cartons: c.cartons,
            collection_reference: c.collection_reference ?? "",
            collection_date: c.collection_date ?? "",
            collection_time: c.collection_time ?? "",
            wip_time: c.wip_time ?? "",
            wip_ready: c.wip_ready ?? false,
            preloads: c.preloads ?? "",
            comments: c.comments ?? "",
            weight_tonnes: c.weight_tonnes ?? undefined,
            non_standard_pallets: c.non_standard_pallets,
            tallest_pallet_height: c.tallest_pallet_height ?? undefined,
            shipping_request_numbers: c.shipping_request_numbers ?? "",
            customer_reference_numbers: c.customer_reference_numbers ?? "",
          }))
        : [emptyCollection()],
    });
    setTimeline(100);
    setMode(openMode);
    setSelected(load);
  };

  const openView = (load: Load) => openLoad(load, "view");
  const openEdit = (load: Load) => openLoad(load, "edit");
  const unlock = (load: Load) =>
    void transition.mutate({ id: load.id, target_status: "draft" });
  const reopen = (load: Load) =>
    void transition.mutate({ id: load.id, target_status: "draft" });

  // Deep-link support: /admin/loads?edit=<id> (and /admin/collections?edit=<id>)
  // opens the load directly in its edit dialog, e.g. from a double-click on a
  // scheduler card reference. The edit form follows the load's own direction.
  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam) return;
    let cancelled = false;
    void (async () => {
      try {
        const load = await getLoad(Number(editParam));
        if (!cancelled) openLoad(load, "edit");
      } catch {
        // The load may have been deleted between the click and the fetch.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam]);

  // Audit deep-link support: /admin/loads?r=<id> opens the load's view dialog
  // with the changed fields highlighted. A deleted load is no longer in the
  // list — it is shown as a ghost copy above the table instead. Deep links
  // for other entities served here (load_invoice) target a different table,
  // so only `load` entries open the load dialog.
  const recordParam = audit.recordId;
  const isLoadEntry = !audit.entityType || audit.entityType === "load";
  useEffect(() => {
    if (!recordParam || ghostRecord || !isLoadEntry) return;
    let cancelled = false;
    void (async () => {
      try {
        const load = await getLoad(Number(recordParam));
        if (!cancelled) openLoad(load, "view");
      } catch {
        // The load may have been deleted between the click and the fetch;
        // with `a=delete` the ghost card covers that case.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordParam, ghostRecord, isLoadEntry]);

  // A collection leg audit entry points at the leg, not the load. Resolve the
  // parent load from the leg's own audit history (its `load_id` column) and
  // open the load's view dialog — the leg card is then boxed in yellow below.
  const isLegEntry = audit.entityType === "load_collection";
  useEffect(() => {
    if (!recordParam || ghostRecord || !isLegEntry) return;
    let cancelled = false;
    void (async () => {
      try {
        const legHistory = await listAudit({
          entity_type: "load_collection",
          entity_id: recordParam,
          limit: 500,
        });
        const loadId = legHistory.items
          .flatMap((entry) => entry.changes)
          .filter((change) => change.field_name === "load_id" && change.new_value)
          .map((change) => change.new_value)[0];
        if (!loadId) return;
        const load = await getLoad(Number(loadId));
        if (!cancelled) openLoad(load, "view");
      } catch {
        // The leg or its load may be gone between the click and the fetch;
        // with `a=delete` the ghost card covers that case.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordParam, ghostRecord, isLegEntry]);

  // When the customer changes, auto-fill the default haulier, CSE and the
  // destination address + postcode from the customer's address. For a
  // collection the customer is the ORIGIN, so the destination is instead left
  // to the destination-site legs (auto-filled from the first leg's site).
  const onCustomerChange = (customerId: number) => {
    const customer = customers.data?.items.find((c) => c.id === customerId);
    setForm((f) => ({
      ...f,
      customer_id: customerId,
      haulier_id: customer?.default_haulier_id ?? null,
      cse_id: customer?.cse_id ?? null,
      destination_address: isCollection
        ? f.destination_address
        : customer?.address?.trim()
          ? customer.address
          : f.destination_address,
      postcode: isCollection
        ? f.postcode
        : customer?.postcode?.trim()
          ? customer.postcode
          : f.postcode,
    }));
  };

  const setCollection = (index: number, patch: Partial<LoadCollectionInput>) => {
    setForm((f) => {
      const collections = [...(f.collections ?? [])];
      collections[index] = { ...collections[index], ...patch };
      // For a collection the destination is a site: mirror the first leg's
      // site address onto the record so it carries a readable destination.
      if (isCollection && patch.site_id) {
        const site = sites.data?.items.find((s) => s.id === patch.site_id);
        if (site?.address) {
          return {
            ...f,
            collections,
            destination_address: f.destination_address || site.address,
            postcode: f.postcode || "",
          };
        }
      }
      return { ...f, collections };
    });
  };

  const addCollection = () => {
    setForm((f) => ({ ...f, collections: [...(f.collections ?? []), emptyCollection()] }));
  };

  const removeCollection = (index: number) => {
    setForm((f) => {
      const collections = [...(f.collections ?? [])];
      collections.splice(index, 1);
      return { ...f, collections: collections.length ? collections : [emptyCollection()] };
    });
  };

  const customerOptions = (customers.data?.items ?? []).map((c) => ({
    value: c.id.toString(),
    label: `${c.customer_no} — ${c.name}`,
  }));
  const haulierOptions = (hauliers.data?.items ?? []).map((h) => ({
    value: h.id.toString(),
    label: h.name,
  }));
  const siteOptions = (sites.data?.items ?? []).map((s) => ({
    value: s.id.toString(),
    label: `${s.site_code} — ${s.name}`,
  }));
  const cseOptions = (cses.data?.items ?? []).map((c) => ({
    value: c.id.toString(),
    label: `${c.name} ${c.surname}`,
  }));
  const trailerOptions = (trailers.data?.items ?? []).map((tr) => ({
    value: tr.id.toString(),
    label: tr.trailer_number,
  }));

  const originLabel = (origin: string) =>
    origin === "csv" ? t("admin.csv") : t("admin.manual");

  const optLabel = (
    options: { value: string; label: string }[],
    id: number | null | undefined,
  ) => (id != null ? (options.find((o) => o.value === String(id))?.label ?? String(id)) : "—");

  const selectedCustomer = customers.data?.items.find((c) => c.id === form.customer_id);

  const formBody = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Left column: Record #, Invoice No, Delivery Date, Arrival Time, Haulier */}
        <div className="col-start-1 row-start-1">
          <TextField
            label={t("admin.record")}
            value={form.load_ref}
            onChange={(v) => setForm({ ...form, load_ref: v })}
            required
          />
        </div>
        <div className="col-start-1 row-start-2">
          <TextField
            label={t("admin.invoiceReference")}
            value={form.invoice_reference ?? ""}
            onChange={(v) => setForm({ ...form, invoice_reference: v })}
          />
        </div>
        <div className="col-start-1 row-start-3 space-y-2">
          <Label>{isCollection ? t("admin.collectionDate") : t("admin.deliveryDate")}</Label>
          <Input
            type="date"
            value={form.delivery_date ?? ""}
            onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
          />
        </div>
        <div className="col-start-1 row-start-4 space-y-2">
          <Label>{isCollection ? t("admin.collectionTime") : t("admin.arrivalTime")}</Label>
          <Input
            type="time"
            value={form.arrival_time ?? ""}
            onChange={(e) => setForm({ ...form, arrival_time: e.target.value })}
          />
        </div>
        <div className="col-start-1 row-start-5">
          <SelectField
            label={t("admin.haulier")}
            value={form.haulier_id ? String(form.haulier_id) : ""}
            onChange={(v) => setForm({ ...form, haulier_id: v ? Number(v) : null })}
            options={[{ value: "", label: "—" }, ...haulierOptions]}
          />
        </div>
        {/* Right column: Customer, Destination (two rows high), Postcode, CSE */}
        <div className="col-start-2 row-start-1">
          <SelectField
            label={isCollection ? t("admin.originCustomer") : t("admin.customer")}
            value={form.customer_id ? String(form.customer_id) : ""}
            onChange={(v) => onCustomerChange(Number(v))}
            options={customerOptions}
          />
        </div>
        <div className="col-start-2 row-span-2 row-start-2">
          <div className="flex h-full flex-col gap-2">
            <Label>
              {isCollection ? t("admin.originAddress") : t("admin.destinationAddress")}
            </Label>
            {isCollection ? (
              <div className="min-h-0 flex-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-2 text-sm text-muted-foreground">
                {selectedCustomer?.address?.trim() || "—"}
              </div>
            ) : (
              <Textarea
                value={form.destination_address ?? ""}
                onChange={(e) => setForm({ ...form, destination_address: e.target.value })}
                className="min-h-0 flex-1 resize-none"
              />
            )}
          </div>
        </div>
        <div className="col-start-2 row-start-4">
          {isCollection ? (
            <Field label={t("admin.postcode")} value={selectedCustomer?.postcode?.trim() || "—"} />
          ) : (
            <TextField
              label={t("admin.postcode")}
              value={form.postcode ?? ""}
              onChange={(v) => setForm({ ...form, postcode: v })}
            />
          )}
        </div>
        <div className="col-start-2 row-start-5">
          <SelectField
            label={t("admin.cse")}
            value={form.cse_id ? String(form.cse_id) : ""}
            onChange={(v) => setForm({ ...form, cse_id: v ? Number(v) : null })}
            options={[{ value: "", label: "—" }, ...cseOptions]}
          />
        </div>
      </div>

      <div className="rounded-md border p-3">
        <div className="mb-3 flex items-center justify-between">
          <Label className="text-sm font-semibold">{t("admin.collectionLegs")}</Label>
          <Button type="button" variant="outline" size="sm" onClick={addCollection}>
            <Plus className="h-4 w-4" /> {t("admin.addLoad")}
          </Button>
        </div>
        <div className="space-y-4">
          {(form.collections ?? []).map((leg, index) => (
            <div key={index} className="rounded-lg border bg-muted/20 p-4">
              <div className="mb-3 flex items-center justify-between">
                <Label className="text-sm font-semibold">
                  {t("admin.load")} {index + 1}
                  {leg.site_id
                    ? ` — ${
                        siteOptions.find((o) => o.value === String(leg.site_id))?.label ?? ""
                      }`
                    : ""}
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => removeCollection(index)}
                  aria-label={t("common.delete")}
                  title={t("common.delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                <SelectField
                  label={t("admin.storageSite")}
                  value={leg.site_id ? String(leg.site_id) : ""}
                  onChange={(v) => setCollection(index, { site_id: v ? Number(v) : null })}
                  options={[{ value: "", label: "—" }, ...siteOptions]}
                />
                <NumberField
                  label={t("admin.palletCount")}
                  value={leg.pallet_count?.toString() ?? ""}
                  onChange={(v) => setCollection(index, { pallet_count: v ? Number(v) : null })}
                />
                <NumberField
                  label={t("admin.cartons")}
                  value={leg.cartons?.toString() ?? ""}
                  onChange={(v) => setCollection(index, { cartons: v ? Number(v) : null })}
                />
                <div className="space-y-2">
                  <Label>{t("admin.wipTime")}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={leg.wip_ready ? "" : (leg.wip_time ?? "")}
                      disabled={leg.wip_ready}
                      onChange={(e) =>
                        setCollection(index, { wip_time: e.target.value, wip_ready: false })
                      }
                      className="flex-1"
                    />
                    <label className="flex shrink-0 items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={leg.wip_ready}
                        onCheckedChange={(v) =>
                          setCollection(index, {
                            wip_ready: v === true,
                            wip_time: v === true ? "" : leg.wip_time,
                          })
                        }
                      />
                      {t("admin.wipReady")}
                    </label>
                  </div>
                </div>
                <TextField
                  label={t("admin.collectionReference")}
                  value={leg.collection_reference ?? ""}
                  onChange={(v) => setCollection(index, { collection_reference: v })}
                />
                <div className="space-y-2">
                  <Label>{t("admin.collectionDate")}</Label>
                  <Input
                    type="date"
                    value={leg.collection_date ?? ""}
                    onChange={(e) => setCollection(index, { collection_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.collectionTime")}</Label>
                  <Input
                    type="time"
                    value={leg.collection_time ?? ""}
                    onChange={(e) => setCollection(index, { collection_time: e.target.value })}
                  />
                </div>
                <SelectField
                  label={t("admin.trailer")}
                  value={leg.trailer_id ? String(leg.trailer_id) : ""}
                  onChange={(v) => setCollection(index, { trailer_id: v ? Number(v) : null })}
                  options={[{ value: "", label: "—" }, ...trailerOptions]}
                />
                <TextField
                  label={t("admin.preloads")}
                  value={leg.preloads ?? ""}
                  onChange={(v) => setCollection(index, { preloads: v })}
                />
                <TextField
                  label={t("admin.comments")}
                  value={leg.comments ?? ""}
                  onChange={(v) => setCollection(index, { comments: v })}
                />
                <NumberField
                  label={t("admin.weightTonnes")}
                  value={leg.weight_tonnes?.toString() ?? ""}
                  onChange={(v) => setCollection(index, { weight_tonnes: v ? Number(v) : null })}
                />
                <CheckboxField
                  label={t("admin.nonStandardPallets")}
                  checked={leg.non_standard_pallets ?? false}
                  onChange={(v) => setCollection(index, { non_standard_pallets: v })}
                />
                <NumberField
                  label={t("admin.tallestPalletHeight")}
                  value={leg.tallest_pallet_height?.toString() ?? ""}
                  onChange={(v) =>
                    setCollection(index, { tallest_pallet_height: v ? Number(v) : null })
                  }
                />
                <TextField
                  label={t("admin.shippingRequests")}
                  value={leg.shipping_request_numbers ?? ""}
                  onChange={(v) => setCollection(index, { shipping_request_numbers: v })}
                  placeholder="comma-separated references"
                />
                <TextField
                  label={t("admin.customerReferenceList")}
                  value={leg.customer_reference_numbers ?? ""}
                  onChange={(v) => setCollection(index, { customer_reference_numbers: v })}
                  placeholder="comma-separated references"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Read-only rendering of the same layout as the edit form, but with plain
  // text values instead of input elements (matches the view dialogs on the
  // other admin pages, e.g. /admin/customers).
  const viewData = selectedDetail.data ?? selected;
  const viewBody = viewData ? (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-start-1 row-start-1">
          <Field label={t("admin.record")} value={viewData.load_ref} field="load_ref" />
        </div>
        <div className="col-start-1 row-start-2">
          <Field
            label={t("admin.invoiceReference")}
            value={viewData.invoice_reference ?? "—"}
            field="invoice_reference"
          />
        </div>
        <div className="col-start-1 row-start-3">
          <Field
            label={isCollection ? t("admin.collectionDate") : t("admin.deliveryDate")}
            value={viewData.delivery_date ?? "—"}
            field="delivery_date"
          />
        </div>
        <div className="col-start-1 row-start-4">
          <Field
            label={isCollection ? t("admin.collectionTime") : t("admin.arrivalTime")}
            value={viewData.arrival_time ?? "—"}
            field="arrival_time"
          />
        </div>
        <div className="col-start-1 row-start-5">
          <Field
            label={t("admin.haulier")}
            value={optLabel(haulierOptions, viewData.haulier_id)}
            field="haulier_id"
          />
        </div>
        <div className="col-start-2 row-start-1">
          <Field
            label={isCollection ? t("admin.originCustomer") : t("admin.customer")}
            value={optLabel(customerOptions, viewData.customer_id)}
            field="customer_id"
          />
        </div>
        <div className="col-start-2 row-span-2 row-start-2">
          <Field
            label={
              isCollection ? t("admin.originAddress") : t("admin.destinationAddress")
            }
            value={
              <span className="whitespace-pre-wrap">
                {isCollection
                  ? optLabel(customerOptions, viewData.customer_id)
                  : viewData.destination_address || "—"}
              </span>
            }
            field="destination_address"
          />
        </div>
        <div className="col-start-2 row-start-4">
          <Field
            label={t("admin.postcode")}
            value={viewData.postcode ?? "—"}
            field="postcode"
          />
        </div>
        <div className="col-start-2 row-start-5">
          <Field
            label={t("admin.cse")}
            value={optLabel(cseOptions, viewData.cse_id)}
            field="cse_id"
          />
        </div>
      </div>

      <div className="rounded-md border p-3">
        <Label className="mb-3 block text-sm font-semibold">{t("admin.collectionLegs")}</Label>
        {(viewData.collections ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <div className="space-y-4">
            {(viewData.collections ?? []).map((leg, index) => (
              <div
                key={index}
                className={`rounded-lg border bg-muted/20 p-4${
                  // The leg an audit deep-link pointed at is boxed in yellow.
                  String(leg.id) === audit.recordId ? " audit-record-box" : ""
                }`}
              >
                <Label className="mb-3 block text-sm font-semibold">
                  {t("admin.load")} {index + 1}
                  {leg.site_id ? ` — ${optLabel(siteOptions, leg.site_id)}` : ""}
                </Label>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <Field label={t("admin.storageSite")} value={optLabel(siteOptions, leg.site_id)} />
                  <Field
                    label={t("admin.palletCount")}
                    value={leg.pallet_count?.toString() ?? "—"}
                  />
                  <Field
                    label={t("admin.cartons")}
                    value={leg.cartons?.toString() ?? "—"}
                  />
                  <Field
                    label={t("admin.wipTime")}
                    value={leg.wip_ready ? t("admin.wipReady") : (leg.wip_time ?? "—")}
                  />
                  <Field
                    label={t("admin.collectionReference")}
                    value={leg.collection_reference ?? "—"}
                  />
                  <Field label={t("admin.collectionDate")} value={leg.collection_date ?? "—"} />
                  <Field label={t("admin.collectionTime")} value={leg.collection_time ?? "—"} />
                  <Field label={t("admin.trailer")} value={optLabel(trailerOptions, leg.trailer_id)} />
                  <Field label={t("admin.preloads")} value={leg.preloads ?? "—"} />
                  <Field label={t("admin.comments")} value={leg.comments ?? "—"} />
                  <Field
                    label={t("admin.weightTonnes")}
                    value={leg.weight_tonnes?.toString() ?? "—"}
                  />
                  <Field
                    label={t("admin.nonStandardPallets")}
                    value={leg.non_standard_pallets ? t("common.yes") : t("common.no")}
                  />
                  <Field
                    label={t("admin.tallestPalletHeight")}
                    value={leg.tallest_pallet_height?.toString() ?? "—"}
                  />
                  <Field
                    label={t("admin.shippingRequests")}
                    value={leg.shipping_request_numbers ?? "—"}
                  />
                  <Field
                    label={t("admin.customerReferenceList")}
                    value={leg.customer_reference_numbers ?? "—"}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  const columns: ColumnDef<Load>[] = [
    {
      accessorKey: "load_ref",
      header: t("admin.record"),
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          {row.original.load_ref}
          {row.original.compatibility && row.original.compatibility.level !== "ok" && (
            <span
              title={row.original.compatibility.codes.join(", ")}
              className="inline-flex shrink-0"
            >
              <TriangleAlert
                className={`h-3.5 w-3.5 ${
                  row.original.compatibility.level === "error" ? "text-destructive" : "text-warning"
                }`}
                aria-label={row.original.compatibility.codes.join(", ")}
              />
            </span>
          )}
        </span>
      ),
    },
    {
      accessorKey: "delivery_date",
      header: t("admin.deliveryDate"),
      cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
    },
    {
      accessorKey: "arrival_time",
      header: t("admin.arrivalTime"),
      cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
    },
    {
      id: "customer",
      header: isCollection ? t("admin.originCustomer") : t("admin.customer"),
      cell: ({ row }) => optLabel(customerOptions, row.original.customer_id),
    },
    {
      accessorKey: "destination_address",
      header: isCollection ? t("admin.destinationSites") : t("admin.destinationAddress"),
      cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
    },
    {
      accessorKey: "postcode",
      header: t("admin.postcode"),
      cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
    },
    {
      id: "haulier",
      header: t("admin.haulier"),
      cell: ({ row }) => optLabel(haulierOptions, row.original.haulier_id),
    },
    {
      accessorKey: "total_pallet_count",
      header: t("admin.palletCount"),
      cell: ({ getValue }) => String(getValue() ?? "—"),
    },
    {
      id: "actions",
      header: t("common.actions"),
      cell: ({ row }) => {
        const status = row.original.status;
        const next = NEXT_BY_STATUS[status] ?? [];
        // Locked and completed loads are read-only unless unlocked/reopened.
        const editable = status !== "locked" && status !== "completed";
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openView(row.original)}>
                <Eye className="h-4 w-4" /> {t("common.view")}
              </DropdownMenuItem>
              {editable ? (
                <DropdownMenuItem onClick={() => openEdit(row.original)}>
                  <Pencil className="h-4 w-4" /> {t("common.edit")}
                </DropdownMenuItem>
              ) : null}
              {status === "locked" ? (
                <DropdownMenuItem onSelect={() => unlock(row.original)}>
                  <LockOpen className="h-4 w-4" /> {t("admin.unlock")}
                </DropdownMenuItem>
              ) : null}
              {next.map((target) => (
                <DropdownMenuItem
                  key={target}
                  onSelect={() =>
                    void transition.mutate({ id: row.original.id, target_status: target })
                  }
                >
                  {t("admin.transition", { to: statusLabel(target) })}
                </DropdownMenuItem>
              ))}
              {status === "completed" && isSuperadmin ? (
                <DropdownMenuItem onSelect={() => reopen(row.original)}>
                  <RefreshCcw className="h-4 w-4" /> {t("admin.reopen")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => setDeleting(row.original)}>
                <Trash2 className="h-4 w-4" /> {t("common.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">
          {isCollection ? t("nav.collections") : t("nav.loads")}
        </h1>
        <Button
          onClick={() => {
            setForm({ ...emptyForm, direction });
            setCreating(true);
          }}
        >
          {t("admin.create")}
        </Button>
      </div>
      {/* Day navigation, aligned left exactly like the load scheduler. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => shiftDay(-1)}
          aria-label="Previous day"
          title="Previous day"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => shiftDay(1)}
          aria-label="Next day"
          title="Next day"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="min-w-0 text-lg font-semibold">
          {day ? formatDateLabel(day) : t("common.all")}
        </div>
        <Input
          type="date"
          value={day}
          onChange={(e) => {
            setDay(e.target.value);
            setOffset(0);
          }}
          className="w-44"
          aria-label={t("admin.deliveryDate")}
        />
        {day ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDay("");
              setOffset(0);
            }}
          >
            {t("common.clear")}
          </Button>
        ) : null}
      </div>

      {!query.isLoading && (query.data?.total ?? 0) === 0 && !ghostRecord ? (
        <EmptyState title={t("common.emptyState")} />
      ) : (
        <>
          {ghostRecord ? (
            <AuditGhostRecord
              entityType={audit.entityType ?? "load"}
              entityId={audit.recordId}
            />
          ) : null}
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            total={query.data?.total ?? 0}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            isLoading={query.isLoading}
            highlightId={highlight}
            onRowDoubleClick={openView}
          />
        </>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("admin.create")}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-4 overflow-y-auto">{formBody}</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={() => saveCreate.mutate()} disabled={!form.load_ref || !form.customer_id}>
              {t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
            // Leaving the deep-linked load also drops its row highlight.
            setHighlight(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDetail.data?.load_ref ?? ""}
              {selectedDetail.data ? (
                <Badge variant={selectedDetail.data.origin === "csv" ? "secondary" : "outline"} className="ml-2">
                  {originLabel(selectedDetail.data.origin)}
                </Badge>
              ) : null}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border p-3">
              <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {t("admin.loadTimeline")}
              </div>
              <Slider
                min={0}
                max={100}
                value={timeline}
                disabled={!hasHistory}
                onChange={(event) => setTimeline(Number(event.target.value))}
                aria-label={t("admin.loadTimeline")}
              />
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>{entries.length ? new Date(timelineStart).toLocaleString() : "—"}</span>
                <span>{entries.length ? new Date(timelineEnd).toLocaleString() : "—"}</span>
              </div>
              {!hasHistory ? (
                <p className="mt-1 text-[10px] text-muted-foreground">{t("admin.noHistory")}</p>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {Object.entries(snapshot)
                    .filter(([key]) =>
                      ["status", "total_pallet_count", "delivery_date", "collection_date", "load_ref"].includes(key),
                    )
                    .map(([key, value]) => (
                      <div key={key} className="flex justify-between gap-2">
                        <span className="text-muted-foreground">{key}</span>
                        <span className="font-mono">{value}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {selected ? (
              <div className="max-h-[55vh] space-y-4 overflow-y-auto">
                {mode === "view" ? viewBody : formBody}
                <Accordion defaultOpen={false}>
                  <AccordionTrigger>{t("admin.loadHistory")}</AccordionTrigger>
                  <AccordionContent>
                    {entries.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{t("admin.noHistory")}</p>
                    ) : (
                      <ul className="space-y-3">
                        {entries.map((entry) => (
                          <li key={entry.id} className="text-xs">
                            <div className="font-medium">
                              {entry.actor_email ?? entry.actor_user_id ?? "system"} · {entry.action} ·{" "}
                              {new Date(entry.occurred_at).toLocaleString()}
                            </div>
                            {entry.changes.length > 0 ? (
                              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                                {entry.changes.map((change, index) => (
                                  <li key={index} className="font-mono">
                                    {change.field_name}: {change.old_value ?? "—"} &gt;{" "}
                                    {change.new_value ?? "—"}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </AccordionContent>
                </Accordion>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelected(null)}>
                {mode === "view" ? t("common.close") : t("common.cancel")}
              </Button>
              {mode === "edit" ? (
                <Button onClick={() => selected && saveUpdate.mutate(selected)}>
                  {t("common.save")}
                </Button>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t("admin.deleteRecord", { name: deleting?.load_ref ?? "" })}
        description={t("admin.deleteRecordDescription")}
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting.id);
        }}
      />
    </div>
  );
}
